import _ from 'lodash';
import React, { Component } from 'react';
import { Button, ButtonGroup, Dropdown, Modal, FormCheck } from 'react-bootstrap';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { isMobile } from 'react-device-detect';
import { ClipLoader } from 'react-spinners';
import ReactTable from 'react-table';
import toastr from '../../../toastr';

// Context menu support
import { Item, Menu, MenuProvider, Separator } from 'react-contexify';

import Label from '../Label';
import LoadingPage from '../../../LoadingPage';
import modal from '../../typings/Modal';
import Model from '../../model/ShopKeeper.model';
import { IConfirmationPlaybackDetail } from '../../typings/Confirmation';
import { ILineItem } from '../../typings/LineItem';

interface IProps {
  container: Object;
  closeConfirmationStatusModal: () => void;
  hide: () => void;
  model: Model;
  show: boolean;
}

// ck1979 represents a "not set" value (and is also an attribution to the orignal author ;))
const blank = 'ck1979';

const contextMenuEvent = isMobile ? 'onClick' : 'onContextMenu';

const soldLineText = 'This line item was previously sold.';
const voidedLineText = 'This line item has been voided.';

// We want case insensitive and searching within strings
// @non-stocking and coment lines need some special lookup logic
const filterOverride = ({ id, value: filterValue }: { id: string; value: string }, row: {}) => {
  const isDescFilter = id === 'detail.shortDescription';
  const isPartNumFilter = id === 'partNum';

  const isCommentLineItem = row['partNum'] === 'LINEITEM_COMMENT';
  const isNonStockingLineItem = row['partNum'] === 'LINEITEM_NONSTOCKINGPART';

  let value = row[id] || '';

  if (isNonStockingLineItem && isPartNumFilter) {
    if (_.get(row, '_original.poFreight', false)) {
      value = 'po-freight';
    } else {
      value = _.get(row, '_original.nonStockingPartNum', '');
      if (!!value) value = `@${value}`;
    }
  }
  if (isNonStockingLineItem && isDescFilter) {
    value = _.get(row, '_original.nonStockingPartDescription', '');
  }
  if (isCommentLineItem && isPartNumFilter) {
    value = _.get(row, '_original.comment', '');
  }

  return value.toLowerCase().includes(filterValue.toLowerCase());
};

@observer
class ConfirmationModal extends Component<IProps, {}> {
  @observable lineItemsSelected = new Set<string>(); // part uniqueIDs
  @observable readOnlyMode = true;
  @observable voidIsProcessing = false;

  confirmationLineItemsFilterParams: Array<{ id: string; value: string }> = [];
  lastSave: Omit<IConfirmationPlaybackDetail, 'confirmationNumber'> | null = null;

  shipIn: HTMLSelectElement;
  shipOut: HTMLSelectElement;
  customerPo: HTMLInputElement;
  contactName: HTMLInputElement;
  contactPhone: HTMLInputElement;
  contactFax: HTMLInputElement;
  contactEmail: HTMLInputElement;
  freight: HTMLInputElement;
  fuel: HTMLInputElement;
  purchasingComments: HTMLInputElement;

  // Since close sends us back to the Backorder Status modal, actions taken here may necessitate a refresh
  statusResetRequiredOnClose = false;

  addSelectedToInvoice = () => {
    this.props.model.addConfirmationPartLineItemsFromActiveConfirmation(this.lineItemsSelected);
  };

  // Sets the confirmation details to editable within the UI
  editDetails = () => {
    this.readOnlyMode = false;
    const model = this.props.model;

    if (model.runMode === 'PIN1') {
      const blanketPo = model.customerParams.purchaseOrderBlanketPO;
      const customerPo = this.customerPo.value.trim();

      if (blanketPo && customerPo.toUpperCase().startsWith(blanketPo.toUpperCase())) {
        this.customerPo.value = customerPo.substring(blanketPo.length + 1); // blanket + ' '
      }
    }
  };

  saveConfirmationChanges = async () => {
    const model = this.props.model;
    const isPIN1 = model.runMode === 'PIN1';

    const blanketPo = isPIN1 ? model.customerParams.purchaseOrderBlanketPO : '';
    const shipIn = this.shipIn.value;
    const shipOut = isPIN1 ? this.shipOut.value : '';
    const contactName = this.contactName.value.trim();
    const contactPhone = this.contactPhone.value.trim();
    const contactFax = this.contactFax.value.trim();
    const contactEmail = this.contactEmail.value.trim();
    const purchasingComments = this.purchasingComments.value.trim();
    const freight = this.freight.value.trim();
    const fuel = ['Y', 'F'].includes(this.props.model.sysParams.promptForFuel) ? this.fuel.value.trim() : '';

    let customerPo = isPIN1 ? this.customerPo.value.trim() : '';

    // Prepend leading blanket PO value if necessary
    if (isPIN1 && blanketPo && !customerPo.toUpperCase().startsWith(blanketPo.toUpperCase())) {
      customerPo = `${blanketPo} ${customerPo}`; // blanket + ' '
    }

    // Only make an API call if there are updates

    const activeConfirmationDetails = _.clone(this.props.model.activeConfirmation!.details);
    activeConfirmationDetails['freightFormatted'] = activeConfirmationDetails.freight
      ? activeConfirmationDetails.freight.toFixed(2)
      : '';
    activeConfirmationDetails['fuelFormatted'] = activeConfirmationDetails.fuel
      ? activeConfirmationDetails.fuel.toFixed(2)
      : '';

    const diff = this.lastSave ? this.lastSave : activeConfirmationDetails;

    if (
      shipIn === diff.shipIn &&
      shipOut === diff.shipOut &&
      customerPo === diff.customerPo &&
      contactName === diff.contactName &&
      contactPhone === diff.contactPhone &&
      contactFax === diff.contactFax &&
      contactEmail === diff.contactEmail &&
      purchasingComments === diff.purchasingComments &&
      freight === diff.freightFormatted &&
      fuel === diff.fuelFormatted
    ) {
      toastr.error('', 'No changes');
      this.readOnlyMode = true;

      if (isPIN1) {
        this.customerPo.value = customerPo; // revert blanket support (if applicable)
      }

      return;
    } else {
      // save changes
      let success = false;

      try {
        await model.updateConfirmation({
          confirmationNumber: model.activeConfirmation!.details.confirmationNumber,
          shipIn,
          shipOut,
          customerPo,
          contactName,
          contactPhone,
          contactFax,
          contactEmail,
          purchasingComments,
          notifyEmail: null,
          freightFormatted: freight,
          fuelFormatted: fuel,
        });
        success = true;
      } catch (e) {
        // model will pop errors
        success = false;
      }

      if (success) {
        this.lastSave = {
          shipIn,
          shipOut,
          customerPo,
          contactName,
          contactPhone,
          contactFax,
          contactEmail,
          purchasingComments,
          freightFormatted: freight,
          fuelFormatted: fuel,
        };
        this.readOnlyMode = true;

        if (isPIN1) {
          this.customerPo.value = customerPo; // revert blanket support (if applicable)
        }

        toastr.success('Success', 'Confirmation Updated');
      } else {
        // stay in "edit" mode
        return;
      }
    }
  };

  /* Note non-tracked (non-stocking) parts are always selectable within this UI
     since clerks often receive & sell non-tracked parts without going through PSO6.
     The PSO6 receiving process, if used, does explicitly mark @parts as "received"
     despite not touching inventory.

     For that reason, this method only selects @parts explicitly received (received > 0),
     while @parts which have not been marked as received will still need to be manually
     selected one at a time.

     PO Freight is always considered received.
     This feature does NOT auto-select comments.

     This feature observe any react-table filters applied.
  */
  selectAllReceived({ includeComments = false } = {}) {
    runInAction(() => {
      const { model } = this.props;
      model.activeConfirmation!.lineItems.forEach(lineItem => {
        if (lineItem.confirmationLineSold || lineItem.confirmationLineVoided) {
          return;
        }

        const isCommentLineItem = lineItem.partNum === 'LINEITEM_COMMENT';
        const isNonStockingLineItem = lineItem.partNum === 'LINEITEM_NONSTOCKINGPART';
        const uniqueID = model.confirmationLineItemUniqueID(lineItem);

        if (
          (lineItem.confirmationSale && (lineItem.confirmationSale.receivedQty! > 0 || lineItem.poFreight)) ||
          (isCommentLineItem && includeComments)
        ) {
          // Only select items from filtered subset, if applied
          if (this.confirmationLineItemsFilterParams.length > 0) {
            const filters = this.confirmationLineItemsFilterParams;

            for (const filter of filters) {
              const filterValue = filter.value;
              let value = _.get(lineItem, filter.id, '');

              // A few lookup overrides on comments and non-tracked @parts (vs. the default react-table accessor)
              if (isCommentLineItem) {
                value = lineItem.comment;
              }
              if (isNonStockingLineItem && filter.id === 'partNum') {
                value = lineItem.poFreight ? 'po-freight' : `@${lineItem.nonStockingPartNum}`;
              }
              if (isNonStockingLineItem && filter.id === 'detail.shortDescription') {
                value = lineItem.nonStockingPartDescription;
              }

              if (!value.toLowerCase().includes(filterValue.toLowerCase())) {
                return; // outter forEach, skip partsChecked.add
              }
            }
          }
          this.lineItemsSelected.add(uniqueID);
        }
      });
    });
  }

  validateConfirmationDetails = () => {
    const model = this.props.model;
    const isPIN1 = model.runMode === 'PIN1';

    const fail = (reason: string, toastId?: string) => {
      if (reason !== '') {
        toastr.error('Error', reason, { toastId });
      }
      return false;
    };

    // PIN1 has different validation rules than the service side
    if (isPIN1) {
      const customerPO = this.customerPo.value.trim();
      if (model.customerParams.purchaseOrderRequired) {
        if (customerPO === '') {
          return fail('Confirmation Customer PO# Required');
        }
        if (!model.validatePo(customerPO)) {
          return fail('Invalid Customer PO format.', 'customerPo'); // blur event will toastr, no need to dupe here
        }
      }

      if (this.contactName.value.trim() === '') {
        return fail('Confirmation Contact Name Not Set');
      }
      if (this.contactPhone.value.trim() === '') {
        return fail('Confirmation Contact Phone Not Set');
      }

      // blur event will pop toastrs - these checks prevents submission
      if (!this.validatePhoneField(this.contactPhone.value)) {
        return fail('Contact phone requires 10 digits.', 'contactPhone');
      }
      if (this.contactFax.value !== '' && !this.validatePhoneField(this.contactFax.value)) {
        return fail('Contact fax requires 10 digits.', 'contactFax');
      }

      // Service side validations
    } else {
      if (this.contactPhone.value !== '' && !this.validatePhoneField(this.contactPhone.value)) {
        return fail('Contact phone requires 10 digits.', 'contactPhone');
      }
      if (this.contactFax.value !== '' && !this.validatePhoneField(this.contactFax.value)) {
        return fail('Contact fax requires 10 digits.', 'contactFax');
      }
    }

    if (this.freight.value.trim() !== '') {
      if (isNaN(parseFloat(this.freight.value))) {
        return fail('Freight must be numeric.', 'freight');
      }
    }

    if (['Y', 'F'].includes(this.props.model.sysParams.promptForFuel)) {
      if (this.fuel.value.trim() !== '') {
        if (isNaN(parseFloat(this.fuel.value))) {
          return fail('Fuel must be numeric.', 'fuel');
        }
      }
    }

    return true;
  };

  // A valid phone number is any string which contains 10 digits, so (702) 530-2199 or 7025302199, for example.
  validatePhoneField = (value: string) => {
    const containsTenDigits = /^\D*(\d\D*){10}$/;
    return containsTenDigits.test(value);
  };

  renderConfirmationCharges = () => {
    return (
      <>
        <div className="sk-confirmationInputCharge">
          <Label>
            Freight
            {this.props.model.activeConfirmation!.details.freightSold ? (
              <span style={{ color: 'green' }} title="This confirmation charge was previously sold">
                {' '}
                - S
              </span>
            ) : (
              ''
            )}
          </Label>
          <input
            type="text"
            defaultValue={
              this.props.model.activeConfirmation!.details.freight
                ? this.props.model.activeConfirmation!.details.freight.toFixed(2)
                : ''
            }
            disabled={this.readOnlyMode || this.props.model.activeConfirmation!.details.freightSold}
            onBlur={() => {
              const freight = this.freight.value.trim();

              if (freight !== '') {
                if (isNaN(parseFloat(freight))) {
                  toastr.error('Error', 'Freight must be numeric.', { toastId: 'freight' });
                } else {
                  // Format the value
                  const formattedFreight = parseFloat(freight).toFixed(2);
                  this.freight.value = formattedFreight;
                }
              }
            }}
            ref={(ref: HTMLInputElement) => (this.freight = ref)}
          />
        </div>
        {['Y', 'F'].includes(this.props.model.sysParams.promptForFuel) && (
          <div className="sk-confirmationInputCharge">
            <Label>
              Fuel
              {this.props.model.activeConfirmation!.details.fuelSold ? (
                <span style={{ color: 'green' }} title="This confirmation charge was previously sold">
                  {' '}
                  - S
                </span>
              ) : (
                ''
              )}
            </Label>
            <input
              type="text"
              defaultValue={
                this.props.model.activeConfirmation!.details.fuel
                  ? this.props.model.activeConfirmation!.details.fuel.toFixed(2)
                  : ''
              }
              disabled={this.readOnlyMode || this.props.model.activeConfirmation!.details.fuelSold}
              onBlur={() => {
                const fuel = this.fuel.value.trim();

                if (fuel !== '') {
                  if (isNaN(parseFloat(fuel))) {
                    toastr.error('Error', 'Fuel must be numeric.', { toastId: 'fuel' });
                  } else {
                    // Format the value
                    const formattedFuel = parseFloat(fuel).toFixed(2);
                    this.fuel.value = formattedFuel;
                  }
                }
              }}
              ref={(ref: HTMLInputElement) => (this.fuel = ref)}
            />
          </div>
        )}
      </>
    );
  };

  render() {
    const { container, hide, model, show } = this.props;

    /* This ostensibly no-op line triggers mobx to re-render on partChecked set changes.
       For example, from "Select All Received".

       The only read of the set is deep within a react-table cell render function that MobX
       can't detect.  We require react-controlled checkbox inputs for react-table filters
       to work correctly, and are using MobX to manage that state with an obsevable set.
       This line tells MobX to observe partsChecked and re-render on changes.
    */
    this.lineItemsSelected.has('mobx-trigger');

    const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(model.runMode);

    return (
      <Modal
        size="lg"
        backdrop="static"
        container={container as any}
        dialogClassName="sk-confirmationModal"
        onHide={() => null}
        show={show}
      >
        <Modal.Body>
          {model.activeConfirmation ? (
            <>
              <div className="sk-confirmationTitle">
                <div>Confirmation {model.activeConfirmation.details.confirmationNumber}</div>
                <div>
                  {model.activeConfirmation.details.timestamp}
                  <span
                    className="sk-confirmationReprint"
                    onClick={() => {
                      model.showBackorderPrinterSelectionModal = true;
                    }}
                    title="Reprint Confirmation"
                  >
                    <i className="fas fa-print" />
                  </span>
                </div>
              </div>

              <fieldset className="sk-confirmation">
                <div className="sk-confirmationDetails">
                  <div className="sk-confirmationInput">
                    <Label>Ship In</Label>
                    <select
                      defaultValue={model.activeConfirmation.details.shipIn}
                      disabled={this.readOnlyMode}
                      ref={(ref: HTMLSelectElement) => (this.shipIn = ref)}
                    >
                      <option disabled={true} value={blank}>
                        &nbsp;
                      </option>
                      {model.sysParams.backorderShipViaCodes.map(x => (
                        <option key={x.code} value={x.code}>
                          ({x.code}) {x.desc}
                        </option>
                      ))}
                    </select>
                  </div>
                  {!isRO && (
                    <>
                      <div className="sk-confirmationInput">
                        <Label>Ship Out</Label>
                        <select
                          defaultValue={model.activeConfirmation.details.shipOut}
                          disabled={this.readOnlyMode}
                          ref={(ref: HTMLSelectElement) => (this.shipOut = ref)}
                        >
                          <option disabled={true} value={blank}>
                            &nbsp;
                          </option>
                          {model.sysParams.shippingCodes.map(x => (
                            <option key={x.code} value={x.code}>
                              ({x.code}) {x.desc}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="sk-confirmationInputCustomerPO">
                        <Label>
                          Customer PO #{' '}
                          {model.sysParams.showPurchaseOrderPattern && model.customerParams.purchaseOrderPatternReq}
                        </Label>
                        <div>
                          {model.customerParams.purchaseOrderBlanketPO && !this.readOnlyMode && (
                            <div className="sk-confirmationBlanketPODisplay">
                              {model.customerParams.purchaseOrderBlanketPO}
                            </div>
                          )}
                          <input
                            type="text"
                            defaultValue={model.activeConfirmation.details.customerPo}
                            disabled={this.readOnlyMode}
                            className={
                              model.customerParams.purchaseOrderBlanketPO && !this.readOnlyMode
                                ? 'sk-confirmationBlanketPOActive'
                                : ''
                            }
                            onBlur={() => {
                              const po = this.customerPo.value.trim();
                              if (po !== '' && !model.validatePo(po)) {
                                toastr.error('Error', 'Invalid Customer PO format.', { toastId: 'customerPo' });
                              }
                            }}
                            ref={(ref: HTMLInputElement) => (this.customerPo = ref)}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {this.renderConfirmationCharges()}
                  <hr />
                  <div className="sk-contactInfoRow">
                    <div className="sk-confirmationInput">
                      <Label>Contact Name</Label>
                      <input
                        type="text"
                        defaultValue={model.activeConfirmation.details.contactName}
                        disabled={this.readOnlyMode}
                        ref={(ref: HTMLInputElement) => (this.contactName = ref)}
                      />
                    </div>
                    <div className="sk-confirmationInput">
                      <Label>Contact Phone</Label>
                      <input
                        type="text"
                        defaultValue={model.activeConfirmation.details.contactPhone}
                        disabled={this.readOnlyMode}
                        onBlur={() => {
                          const phone = this.contactPhone.value.trim();
                          if (phone !== '' && !this.validatePhoneField(phone)) {
                            toastr.error('Error', 'Contact phone requires 10 digits.', { toastId: 'contactPhone' });
                          }
                        }}
                        ref={(ref: HTMLInputElement) => (this.contactPhone = ref)}
                      />
                    </div>
                    <div className="sk-confirmationInput">
                      <Label>Contact Fax</Label>
                      <input
                        type="text"
                        defaultValue={model.activeConfirmation.details.contactFax}
                        disabled={this.readOnlyMode}
                        onBlur={() => {
                          const fax = this.contactFax.value.trim();
                          if (fax !== '' && !this.validatePhoneField(fax)) {
                            toastr.error('Error', 'Contact fax requires 10 digits.', { toastId: 'contactFax' });
                          }
                        }}
                        ref={(ref: HTMLInputElement) => (this.contactFax = ref)}
                      />
                    </div>
                    <div className="sk-confirmationInput">
                      <Label>Contact E-mail</Label>
                      <input
                        type="text"
                        maxLength={50}
                        defaultValue={model.activeConfirmation.details.contactEmail}
                        disabled={this.readOnlyMode}
                        ref={(ref: HTMLInputElement) => (this.contactEmail = ref)}
                      />
                    </div>
                  </div>
                  <Label>Purchasing Comments</Label>
                  <input
                    type="text"
                    style={{ width: '98%' }}
                    maxLength={50}
                    defaultValue={model.activeConfirmation.details.purchasingComments}
                    disabled={this.readOnlyMode}
                    ref={(ref: HTMLInputElement) => (this.purchasingComments = ref)}
                  />
                </div>
                <div className="sk-confirmationActions">
                  {this.readOnlyMode ? (
                    <Button variant="secondary" size="sm" onClick={this.editDetails}>
                      Edit Details
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => {
                        if (this.validateConfirmationDetails()) {
                          this.saveConfirmationChanges();
                        }
                      }}
                    >
                      Save Changes
                    </Button>
                  )}
                </div>
              </fieldset>

              <div className="sk-confirmationPartLines">
                <ReactTable
                  className="-striped"
                  data={model.activeConfirmation.lineItems}
                  defaultFilterMethod={filterOverride}
                  defaultPageSize={model.activeConfirmation.lineItems.length}
                  onFilteredChange={filterParams => (this.confirmationLineItemsFilterParams = filterParams)}
                  showPagination={false}
                  style={{
                    maxHeight: '320px',
                  }}
                  getTrProps={(state: any, rowInfo: any) => {
                    if (_.get(rowInfo, 'original.confirmationLineSold', false)) {
                      return {
                        style: {
                          background: '#efe',
                        },
                      };
                    }

                    if (_.get(rowInfo, 'original.confirmationLineVoided', false)) {
                      return {
                        style: {
                          background: '#fee',
                        },
                      };
                    }

                    if (_.get(rowInfo, 'original.confirmationSale.useOnHand', false)) {
                      return {
                        style: {
                          background: '#ffedcc',
                        },
                      };
                    }

                    return {};
                  }}
                  columns={[
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        const uniqueID = model.confirmationLineItemUniqueID(lineItem);
                        const addedToInvoice = model.confirmationPartsAddedToInvoice.has(uniqueID);

                        // The order of these checks matters.  Do not alter.

                        // Sold in a previous completed invoice transaction
                        if (lineItem.confirmationLineSold) {
                          return (
                            <div
                              onContextMenu={evt => evt.preventDefault()}
                              style={{ color: 'green', fontWeight: 'bold' }}
                              title={soldLineText}
                            >
                              S
                            </div>
                          );
                        }

                        // Previously voided - shown here for historical reference, not possible to un-void
                        if (lineItem.confirmationLineVoided) {
                          return (
                            <div
                              onContextMenu={evt => evt.preventDefault()}
                              style={{ color: 'red', fontWeight: 'bold' }}
                              title={voidedLineText}
                            >
                              V
                            </div>
                          );
                        }

                        // Daily orders in a "pre" order state cannot be sold nor force received
                        if (
                          lineItem.confirmationSale &&
                          lineItem.confirmationSale.poNum.startsWith('PRE') &&
                          !lineItem.confirmationSale!.useOnHand
                        ) {
                          return null;
                        }

                        // Do not allow selection of non-received stocking parts unless use on hand is selected
                        if (
                          lineItem.detail &&
                          lineItem.confirmationSale!.receivedQty === 0 &&
                          !lineItem.confirmationSale!.useOnHand
                        ) {
                          return null;
                        }

                        /* Non-tracking (non-stocked) parts can be sold any time whether they are marked
                           as "received" (via PSO6), or are being received by the clerk in real time. No
                           force-receive is necessary in the latter case.  Note "Select All Received" only
                           covers the former case.
                        */

                        // Received / Force-Received stocking parts, non-tracked parts, memos/comment
                        return (
                          <div onContextMenu={evt => evt.preventDefault()}>
                            <FormCheck inline={true}>
                              <FormCheck.Input
                                type="checkbox"
                                className="sk-useMaterialInput"
                                disabled={addedToInvoice}
                                onChange={(evt: any) => {
                                  if (evt.target.checked) {
                                    this.lineItemsSelected.add(uniqueID);
                                  } else {
                                    this.lineItemsSelected.delete(uniqueID);
                                  }
                                }}
                                checked={this.lineItemsSelected.has(uniqueID) || addedToInvoice}
                              />
                            </FormCheck>
                          </div>
                        );
                      },
                      Header: ' ',
                      style: {
                        textAlign: 'center',
                      },
                      width: 30,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        let result;
                        if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
                          result = lineItem.poFreight ? 'PO-FREIGHT' : `@${lineItem.nonStockingPartNum}`;
                        } else if (lineItem.partNum === 'LINEITEM_COMMENT') {
                          result = (
                            <span className="sk-partLineComment" title={lineItem.comment}>
                              {lineItem.comment}
                            </span>
                          );
                        } else {
                          result = lineItem.partNum;
                        }

                        let uniqueID: string, poNum: string, voidLineID: string, voidLineText: string;

                        if (lineItem.confirmationSale) {
                          uniqueID = lineItem.confirmationSale.uniqueID!;
                          poNum = lineItem.confirmationSale.poNum;
                          voidLineID = lineItem.confirmationSale.lineNum;
                          if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
                            if (lineItem.poFreight) {
                              voidLineText = `FREIGHT:${lineItem.confirmationSale!.poNum}`;
                            } else {
                              voidLineText = `@${lineItem.nonStockingPartNum}`;
                            }
                          } else {
                            voidLineText = lineItem.partNum;
                          }

                          // Otherwise this is a PO comment, which uses slightly different rules on voids
                          // Then unique ID is provded as PO.C# format.  The QM granular void system wants only C#.
                        } else {
                          uniqueID = lineItem.confirmationComment!.uniqueID;
                          poNum = lineItem.confirmationComment!.poNum;

                          const commentPosition = uniqueID.match(/.(C\d+$)/);
                          voidLineID = commentPosition ? commentPosition[1] : 'INVALIDCOMMENTPOSITION';
                          voidLineText = 'PO MEMO';
                        }

                        // previously sold or voided
                        const soldOrVoided = lineItem.confirmationLineSold || lineItem.confirmationLineVoided;

                        // we will enable useful stocking part modals regardless of status (sold/voided)
                        const stockingPart = !['LINEITEM_NONSTOCKINGPART', 'LINEITEM_COMMENT'].includes(
                          lineItem.partNum
                        );

                        // stocking parts have the lineItem property paths in question
                        const useOnHandOption =
                          stockingPart && lineItem.confirmationSale!.receivedQty === 0 && lineItem.detail!.onHand > 0;

                        const partialOnHand = useOnHandOption
                          ? lineItem.detail!.onHand < lineItem.confirmationSale!.confirmationQty
                          : false;

                        return (
                          <>
                            {soldOrVoided && !stockingPart ? (
                              <div onContextMenu={evt => evt.preventDefault()}>{result}</div>
                            ) : (
                              <MenuProvider event={contextMenuEvent} id={uniqueID}>
                                {result}
                              </MenuProvider>
                            )}
                            <Menu id={uniqueID} animation="fade">
                              {stockingPart && (
                                <>
                                  <Item
                                    onClick={() => {
                                      modal.open('partDetail', { lookupKey: lineItem.partNum });
                                    }}
                                  >
                                    Part Detail
                                  </Item>
                                  <Item
                                    onClick={() => {
                                      modal.open('partHistory', { lookupKey: lineItem.partNum });
                                    }}
                                  >
                                    Part History
                                  </Item>
                                  {!soldOrVoided && <Separator />}
                                </>
                              )}
                              {!soldOrVoided && useOnHandOption && (
                                <>
                                  <Item
                                    onClick={() => {
                                      lineItem.confirmationSale!.useOnHand = true;
                                      this.lineItemsSelected.add(uniqueID);
                                      this.forceUpdate();
                                    }}
                                  >
                                    Use {partialOnHand ? 'Partial ' : ''}On-Hand Stock to Fill
                                  </Item>
                                  <Separator />
                                </>
                              )}
                              {!soldOrVoided && (
                                <Item
                                  onClick={async () => {
                                    const confirm: boolean = await new Promise(resolve => {
                                      modal.confirm({
                                        title: 'Void Line Item',
                                        message: `Are you sure you wish to void confirmation line ${
                                          props.index + 1
                                        }: ${voidLineText}?`,
                                        confirmLabels: ['Yes', 'No'],
                                        callback: () => resolve(true),
                                        onClose: () => resolve(false),
                                      });
                                    });

                                    if (confirm) {
                                      this.voidIsProcessing = true;
                                      if (
                                        await model.voidFromActiveConfirmation([
                                          { poNum, voidLineIDs: [voidLineID], uniqueIDs: [uniqueID] },
                                        ])
                                      ) {
                                        lineItem.confirmationLineVoided = true;
                                        this.statusResetRequiredOnClose = true;
                                      }
                                      this.voidIsProcessing = false;
                                    }
                                  }}
                                >
                                  Void Confirmation Line: {props.index + 1}
                                </Item>
                              )}
                            </Menu>
                          </>
                        );
                      },
                      Header: 'Part',
                      accessor: 'partNum', // partNum, LINEITEM_NONSTOCKINGPART, or LINEITEM_COMMENT
                      width: 180,
                      filterable: true,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        let result;
                        if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
                          result = lineItem.nonStockingPartDescription;
                        } else if (lineItem.partNum === 'LINEITEM_COMMENT') {
                          result = '-';
                        } else {
                          const description = lineItem.detail!.shortDescription
                            ? lineItem.detail!.shortDescription
                            : lineItem.detail!.longDescription;
                          result = <span title={description}>{description}</span>;
                        }

                        const id = lineItem.confirmationSale
                          ? lineItem.confirmationSale.uniqueID!
                          : lineItem.confirmationComment!.uniqueID;

                        return <div onContextMenu={evt => evt.preventDefault()}>{result}</div>;
                      },
                      Header: 'Description',
                      accessor: 'detail.shortDescription',
                      filterable: true,
                      width: 180,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        const useOnHand = _.get(lineItem, 'confirmationSale.useOnHand', false);

                        let result;
                        if (lineItem.partNum === 'LINEITEM_COMMENT' || lineItem.poFreight) {
                          result = '-';
                        } else {
                          result = lineItem.quantity;
                        }

                        return (
                          <div
                            style={useOnHand ? { color: 'darkorange', fontWeight: 'bold' } : {}}
                            onContextMenu={evt => evt.preventDefault()}
                          >
                            {result}
                          </div>
                        );
                      },
                      Header: () => <span title="Ordered Quantity">OQTY</span>,
                      accessor: 'quantity',
                      style: {
                        textAlign: 'center',
                      },
                      width: 65,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        let result;
                        if (lineItem.partNum === 'LINEITEM_COMMENT' || lineItem.poFreight) {
                          result = '-';
                        } else {
                          result = lineItem.confirmationSale!.boQty;
                        }
                        return <div onContextMenu={evt => evt.preventDefault()}>{result}</div>;
                      },
                      Header: () => <span title="Backordred by Vendor">BO</span>,
                      accessor: 'confirmationSale.boQty',
                      style: {
                        textAlign: 'center',
                      },
                      width: 65,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        let result;
                        if (lineItem.partNum === 'LINEITEM_COMMENT' || lineItem.poFreight) {
                          result = '-';
                        } else {
                          result = lineItem.confirmationSale!.receivedQty;
                        }
                        return <div onContextMenu={evt => evt.preventDefault()}>{result}</div>;
                      },
                      Header: () => <span title="Received Quantity">RCD</span>,
                      accessor: 'confirmationSale.receivedQty',
                      style: {
                        textAlign: 'center',
                      },
                      width: 65,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        const useOnHand = _.get(lineItem, 'confirmationSale.useOnHand', false);

                        const partialOnHand = useOnHand
                          ? lineItem.detail!.onHand < lineItem.confirmationSale!.confirmationQty
                          : false;

                        let result;
                        if (
                          lineItem.partNum === 'LINEITEM_COMMENT' ||
                          lineItem.partNum === 'LINEITEM_NONSTOCKINGPART' ||
                          lineItem.poFreight
                        ) {
                          result = '-';
                        } else {
                          result = lineItem.detail!.onHand;
                        }
                        return (
                          <div
                            style={useOnHand ? { color: partialOnHand ? 'red' : 'green', fontWeight: 'bold' } : {}}
                            onContextMenu={evt => evt.preventDefault()}
                          >
                            {result}
                          </div>
                        );
                      },
                      Header: () => <span title="On-Hand Stock">OH</span>,
                      accessor: 'detail.onHand',
                      style: {
                        textAlign: 'center',
                      },
                      width: 65,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        const poNum = lineItem.confirmationSale
                          ? lineItem.confirmationSale.poNum
                          : lineItem.confirmationComment!.poNum;

                        const result = /^HODC|^PRE/.test(poNum) ? (
                          poNum
                        ) : (
                          <span
                            className="sk-actionLink"
                            onClick={() =>
                              modal.open('purchaseOrderDetail', {
                                lookupKey: poNum,
                                title: `PO Detail: ${poNum}`,
                              })
                            }
                          >
                            {poNum}
                          </span>
                        );

                        const id = `po${
                          lineItem.confirmationSale
                            ? lineItem.confirmationSale.uniqueID!
                            : lineItem.confirmationComment!.uniqueID
                        }`;

                        const isHODC = /^HODC/.test(poNum);

                        return (
                          <>
                            {lineItem.confirmationLineSold || lineItem.confirmationLineVoided ? (
                              <div onContextMenu={evt => evt.preventDefault()}>{result}</div>
                            ) : (
                              <MenuProvider event={contextMenuEvent} id={id}>
                                {result}
                              </MenuProvider>
                            )}
                            <Menu id={id} animation="fade">
                              <Item
                                onClick={async () => {
                                  const confirm: boolean = await new Promise(resolve => {
                                    modal.confirm({
                                      title: `Void ${isHODC ? 'Hold' : 'Purchase Order'}`,
                                      message: `Are you sure you wish to void ${isHODC ? 'Hold' : 'PO'}: ${poNum}?`,
                                      confirmLabels: ['Yes', 'No'],
                                      callback: () => resolve(true),
                                      onClose: () => resolve(false),
                                    });
                                  });

                                  if (confirm) {
                                    this.voidIsProcessing = true;
                                    if (await model.voidFromActiveConfirmation([{ poNum }])) {
                                      model.activeConfirmation!.lineItems.forEach(item => {
                                        if (
                                          (item.confirmationSale && item.confirmationSale.poNum === poNum) ||
                                          (item.confirmationComment && item.confirmationComment.poNum === poNum)
                                        ) {
                                          item.confirmationLineVoided = true;
                                        }
                                      });
                                      this.statusResetRequiredOnClose = true;
                                    }
                                    this.voidIsProcessing = false;
                                  }
                                }}
                              >
                                Void {isHODC ? 'Hold' : 'PO'}: {poNum}
                              </Item>
                            </Menu>
                          </>
                        );
                      },
                      Header: 'PO #',
                      accessor: 'confirmationSale.poNum',
                      filterable: true,
                      style: {
                        textAlign: 'center',
                      },
                      width: 100,
                    },
                    {
                      Cell: props => {
                        const lineItem = props.original as ILineItem;
                        let result;
                        if (lineItem.partNum === 'LINEITEM_COMMENT') {
                          result = '-';
                        } else if (lineItem.partNum === 'LINEITEM_NONSTOCKINGPART') {
                          // this captures freight lines as well
                          result = lineItem.nonStockingPartTradePrice!.toFixed(2);
                        } else {
                          result = lineItem.detail!.tradePrice.toFixed(2);
                        }
                        return <div onContextMenu={evt => evt.preventDefault()}>{result}</div>;
                      },
                      Header: 'Sell Price',
                      accessor: 'detail.tradePrice', // see Cell def, this isn't alwasy used
                      style: {
                        textAlign: 'right',
                      },
                      width: 80,
                    },
                  ]}
                />
              </div>
            </>
          ) : (
            <div style={{ paddingTop: '50px' }}>
              <LoadingPage />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="sk-confirmationModalFooter">
            <div>
              {model.activeConfirmation ? (
                <div className="sk-confirmationModalActionButtons">
                  <Button variant="success" disabled={this.voidIsProcessing} onClick={this.addSelectedToInvoice}>
                    {isRO ? 'Add Selected To Op' : 'Add Selected To Invoice'}
                  </Button>
                  <Dropdown drop="up" as={ButtonGroup}>
                    <Button
                      variant="secondary"
                      id="split-button-dropup"
                      disabled={this.voidIsProcessing}
                      onClick={() => this.selectAllReceived()}
                    >
                      Select All Received
                    </Button>

                    <Dropdown.Toggle split={true} variant="secondary" id="split-button-dropup" />

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => this.selectAllReceived({ includeComments: true })}>
                        Include Comments
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button
                    variant="danger"
                    disabled={this.voidIsProcessing}
                    onClick={async () => {
                      const confirm: boolean = await new Promise(resolve => {
                        modal.confirm({
                          title: 'Void Confirmation',
                          message: `Are you sure you wish to void ALL lines on confirmation ${
                            model.activeConfirmation!.details.confirmationNumber
                          }?`,
                          confirmLabels: ['Yes', 'No'],
                          callback: () => resolve(true),
                          onClose: () => resolve(false),
                        });
                      });

                      if (confirm) {
                        this.voidIsProcessing = true;
                        if (await model.voidFromActiveConfirmation()) {
                          hide();
                          // Refresh confirmation data.  Backorder status modal will react to this automatically.
                          model.loadConfirmationStatusDetail();
                        }
                        this.voidIsProcessing = false;
                      }
                    }}
                  >
                    {this.voidIsProcessing ? (
                      <>
                        <ClipLoader size={13} color="white" /> VOID PROCESSING
                      </>
                    ) : (
                      'VOID CONFIRMATION'
                    )}
                  </Button>
                  {isRO && (
                    <Button
                      variant="warning"
                      title="Change Active Operation"
                      onClick={() => {
                        model.uiBridge.showOperationSelectionModal = true;
                      }}
                    >
                      OP: {model.activeOperation || 'S'}
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={() => {
                  this.props.closeConfirmationStatusModal();
                  if (this.statusResetRequiredOnClose) {
                    // Refresh confirmation data for next status run
                    model.loadConfirmationStatusDetail();
                  }
                  hide();
                }}
              >
                Back to {isRO ? 'RO' : 'Invoice'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  if (this.statusResetRequiredOnClose) {
                    // Refresh confirmation data for next status run.
                    // Underlying backorder status modal will react to this automatically.
                    model.loadConfirmationStatusDetail();
                  }
                  hide();
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ConfirmationModal;
